<template>
  <v-menu
    v-model="showMenu"
    location="bottom"
    :close-on-content-click="false"
    :persistent="!closeOnClick"
  >
    <template #activator="{ props }">
      <v-text-field
        v-model="dateRangeText"
        label="Date Range"
        prepend-inner-icon="mdi-calendar"
        readonly
        hide-details
        variant="outlined"
        density="compact"
        class="daterange-picker-field"
        style="width: 250px;"
        @click="showMenu = true"
        v-bind="{ ...$attrs, ...props }"
      ></v-text-field>
    </template>

    <v-card>
      <!--
        Vuetify 2 used `range`. Vuetify 3 uses multiple="range" and may emit
        every day between start and end — we store/emit [start, end] like master.
      -->
      <v-date-picker
        :model-value="pickerModelValue"
        multiple="range"
        @update:model-value="changeDateRange($event)"
      ></v-date-picker>
      <div
        class="d-flex"
      >
        <base-sidebar-btn
          @click="confirmDateRange"
          label="Confirm"
          icon="mdi-check"
          :btn-class="!$store.state.isDarkMode ? 'blue-darken-2' : 'pink-lighten-2'"
          :icon-color="!$store.state.isDarkMode ? 'white' : 'white'"
        ></base-sidebar-btn>
        <v-spacer />
        <base-sidebar-btn
          @click="clearDateRange()"
          label="Reset"
          icon="mdi-cancel"
          :btn-class="!$store.state.isDarkMode ? 'grey-lighten-5' : 'grey-darken-4'"
          :icon-color="!$store.state.isDarkMode ? 'grey' : 'white'"
        ></base-sidebar-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: 'BaseDaterangePicker',

  inheritAttrs: false,

  props: {
    modelValue: {
      type: Array,
      default: undefined,
    },
    value: {
      type: Array,
      default: undefined,
    },
    lastDays: {
      type: [Number, String],
      default: 0,
    },
    closeOnClick: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue', 'input', 'confirm'],

  data: () => ({
    showMenu: false,
  }),

  computed: {
    dateRangeValue() {
      return this.modelValue ?? this.value ?? [];
    },

    dateRangeText() {
      const range = this.dateRangeValue;
      if (!range.length) {
        return '';
      }
      if (range.length === 1) {
        return range[0];
      }
      return `${range[0]} to ${range[range.length - 1]}`;
    },

    /**
     * V3 range highlighting selects days present in the model array.
     * Expand [start, end] strings into Date[] so the calendar shows the span.
     */
    pickerModelValue() {
      const range = this.dateRangeValue;
      if (!range.length) {
        return [];
      }
      const start = this.toPickerDate(range[0]);
      if (!start) {
        return [];
      }
      if (range.length === 1) {
        return [start];
      }
      const end = this.toPickerDate(range[range.length - 1]);
      if (!end) {
        return [start];
      }
      return this.expandDates(start, end);
    },
  },

  mounted() {
    this.initDateRange();
  },

  methods: {
    emitDateRange(value) {
      this.$emit('update:modelValue', value);
      this.$emit('input', value);
    },

    initDateRange: function () {
      if (this.lastDays && !this.dateRangeValue.length) {
        var start_date = new Date();
        start_date.setDate(start_date.getDate() - parseInt(this.lastDays, 10));
        this.emitDateRange([
          this.formatDate(start_date),
          this.formatDate(new Date()),
        ]);
      }
    },

    changeDateRange: function (value) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        this.emitDateRange([]);
        return;
      }

      const list = Array.isArray(value) ? value : [value];
      const normalized = list
        .map((item) => this.normalizeDateValue(item))
        .filter(Boolean);

      if (normalized.length <= 1) {
        this.emitDateRange(normalized);
        return;
      }

      // V3 multiple="range" emits every day in the span; master / API use [start, end]
      let start = normalized[0];
      let end = normalized[normalized.length - 1];
      if (new Date(start) > new Date(end)) {
        const tmp = start;
        start = end;
        end = tmp;
      }
      this.emitDateRange([start, end]);
    },

    normalizeDateValue: function (val) {
      if (val == null || val === '') {
        return '';
      }
      if (val instanceof Date && !Number.isNaN(val.getTime())) {
        return this.formatDate(val);
      }
      if (typeof val === 'string') {
        const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) {
          return m[1];
        }
      }
      const parsed = new Date(val);
      if (!Number.isNaN(parsed.getTime())) {
        return this.formatDate(parsed);
      }
      return '';
    },

    toPickerDate: function (val) {
      const day = this.normalizeDateValue(val);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
        return null;
      }
      const [y, m, d] = day.split('-').map(Number);
      return new Date(y, m - 1, d);
    },

    expandDates: function (start, end) {
      const dates = [];
      const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      if (cur > last) {
        return this.expandDates(end, start);
      }
      while (cur <= last) {
        dates.push(new Date(cur.getFullYear(), cur.getMonth(), cur.getDate()));
        cur.setDate(cur.getDate() + 1);
      }
      return dates;
    },

    formatDate: function (date) {
      return date.getFullYear() + '-' + ('0'+(date.getMonth() + 1)).slice(-2) + '-' + ('0'+(date.getDate())).slice(-2);
    },

    clearDateRange: function () {
      this.showMenu = false;
      // Force re-init even if a range is already set (master reset behavior)
      if (this.lastDays) {
        var start_date = new Date();
        start_date.setDate(start_date.getDate() - parseInt(this.lastDays, 10));
        this.emitDateRange([
          this.formatDate(start_date),
          this.formatDate(new Date()),
        ]);
      }
      setTimeout(() => {
        this.$emit('confirm', this.dateRangeValue);
      }, 500);
    },

    confirmDateRange: function () {
      this.showMenu = false;
      this.$emit('confirm', this.dateRangeValue);
    }
  }
};
</script>

<style scoped>
.daterange-picker-field :deep(.v-field__prepend-inner) {
  padding-inline-end: 4px;
}
</style>
